import React, { useEffect } from 'react';
import { Form, Select } from 'formik-antd';
import { Row, Col } from 'antd';
import { FormikProps, Formik, prepareDataForValidation } from 'formik';
import * as Yup from 'yup';

import Radio from 'components/formik/Radio';
import FormikEffect from 'components/formik/FormikEffect';
import FormItem from 'components/formik/FormItem';
import InputNumber from 'components/formik/InputNumber';
import { getAllIndustries } from 'redux/reducers/qualificationSlice';
import {
  industriesSelector,
  isIndustriesLoadingSelector,
} from 'redux/selectors/qualificationSelectors';
import {
  CustomerSizeEnum,
  YearsInBusinessEnum,
  AbilityToPayEnum,
} from 'api/backofficeApiClient';
import { DurationTypeEnum } from 'api/marketplaceApiClient';
import {
  getClientSizes,
  getYearsInBusiness,
  getAbilityToPay,
} from 'utils/clientUtils';
import FormikAutoCompleteWithLoading from 'hocs/withLoading/FormikAutoCompleteWithLoading';
import { useAppDispatch } from 'hooks/useAppDispatch';
import { useAppSelector } from 'hooks/useAppSelector';

import styles from './AdditionalInfo.module.less';

type Props = {
  formRef: any;
  onChange: (formik: FormikProps<AdditionalInfoFormValues>) => void;
  initialValues?: AdditionalInfoFormValues;
};

export interface AdditionalInfoFormValues {
  clientSize?: CustomerSizeEnum;
  industry: string;
  haveBusinessBefore: boolean;
  yearsInBusiness?: YearsInBusinessEnum;
  abilityToPay?: AbilityToPayEnum;
  billRate: number | null;
  duration: number | null;
  durationUnit: DurationTypeEnum;
}

export const isAdditionalInfoFormValidForDraft = (
  formik: FormikProps<AdditionalInfoFormValues>,
) => {
  const dataForValidation = prepareDataForValidation(formik.values);

  return draftValidationSchema.isValidSync(dataForValidation);
};

const initialValidationSchema = {
  clientSize: Yup.number(),
  industry: Yup.string()
    .trim()
    .max(120, 'Industry should be 120 characters maximum'),
  haveBusinessBefore: Yup.boolean(),
  yearsInBusiness: Yup.number(),
  abilityToPay: Yup.number(),
  billRate: Yup.number()
    .nullable()
    .min(0, 'Bill rate should be from 0 to 999999')
    .max(999999, 'Bill rate should be from 0 to 999999'),
  duration: Yup.number()
    .nullable()
    .min(1, 'Length should be from 1 to 999')
    .max(999, 'Length should be from 1 to 999'),
  durationUnit: Yup.number(),
};

const draftValidationSchema = Yup.object(initialValidationSchema);
const submitValidationSchema = Yup.object(initialValidationSchema);

export default React.memo((props: Props) => {
  const { formRef, onChange, initialValues: predefinedInitialValues } = props;
  const dispatch = useAppDispatch();
  const industries = useAppSelector(industriesSelector);
  const isIndustriesLoading = useAppSelector(isIndustriesLoadingSelector);

  const initialValues: AdditionalInfoFormValues = {
    clientSize: undefined,
    industry: '',
    haveBusinessBefore: false,
    yearsInBusiness: undefined,
    abilityToPay: undefined,
    billRate: null,
    duration: null,
    durationUnit: DurationTypeEnum.Week,
  };

  useEffect(() => {
    dispatch(getAllIndustries());
  }, [dispatch]);

  return (
    <Formik
      enableReinitialize
      innerRef={formRef}
      initialValues={predefinedInitialValues || initialValues}
      validationSchema={submitValidationSchema}
      onSubmit={() => {}}
    >
      <>
        <FormikEffect onChange={onChange} />

        <Form layout="vertical">
          <div className="modal-title">Additional information</div>

          <Row gutter={40}>
            <Col xs={24} md={12}>
              <FormItem
                name="clientSize"
                label="Potential headcount"
                data-id="newClientClientSizeField"
              >
                <Select name="clientSize" placeholder="Select customer size">
                  {getClientSizes().map((clientSize) => (
                    <Select.Option
                      key={`select-client-size-${clientSize.value}`}
                      value={clientSize.value}
                    >
                      {clientSize.label}
                    </Select.Option>
                  ))}
                </Select>
              </FormItem>
            </Col>

            <Col xs={24} md={12}>
              <FormItem
                name="industry"
                label="Industry"
                data-id="newClientIndustryField"
              >
                <FormikAutoCompleteWithLoading
                  isLoading={isIndustriesLoading}
                  name="industry"
                  filterOption
                  placeholder="Select customer industry"
                  showSearch
                >
                  {industries.map((industry) => (
                    <FormikAutoCompleteWithLoading.Option
                      key={`select-industry-${industry.id}`}
                      value={industry.name || ''}
                    >
                      {industry.name}
                    </FormikAutoCompleteWithLoading.Option>
                  ))}
                </FormikAutoCompleteWithLoading>
              </FormItem>
            </Col>
          </Row>

          <Row gutter={40}>
            <Col xs={24} md={12}>
              <FormItem
                name="yearsInBusiness"
                label="Years in business"
                data-id="newClientYearsInBusinessField"
              >
                <Select
                  name="yearsInBusiness"
                  placeholder="Select years in business"
                >
                  {getYearsInBusiness().map((year) => (
                    <Select.Option
                      key={`select-years-in-business-${year.value}`}
                      value={year.value}
                    >
                      {year.label}
                    </Select.Option>
                  ))}
                </Select>
              </FormItem>
            </Col>

            <Col xs={24} md={12}>
              <div>Estimated length of the project</div>
              <Row gutter={20}>
                <Col flex="120px">
                  <FormItem data-id="newClientDurationField" name="duration">
                    <InputNumber name="duration" placeholder="Enter length" />
                  </FormItem>
                </Col>

                <Col flex="auto">
                  <FormItem
                    data-id="newClientDurationUnitField"
                    className="duration-unit"
                    name="durationUnit"
                  >
                    <Radio.Group name="durationUnit">
                      <Radio name="durationUnit" value={DurationTypeEnum.Week}>
                        Week(s)
                      </Radio>

                      <Radio name="durationUnit" value={DurationTypeEnum.Month}>
                        Month(s)
                      </Radio>

                      <Radio name="durationUnit" value={DurationTypeEnum.Year}>
                        Year(s)
                      </Radio>
                    </Radio.Group>
                  </FormItem>
                </Col>
              </Row>
            </Col>
          </Row>

          <Row gutter={40}>
            <Col xs={24} md={12}>
              <FormItem
                name="abilityToPay"
                label="Ability to pay"
                data-id="newClientAbilityToPayField"
              >
                <Select name="abilityToPay" placeholder="Select ability to pay">
                  {getAbilityToPay().map((ability) => (
                    <Select.Option
                      key={`select-ability-to-pay-${ability.value}`}
                      value={ability.value}
                    >
                      {ability.label}
                    </Select.Option>
                  ))}
                </Select>
              </FormItem>
            </Col>

            <Col xs={24} md={12}>
              <FormItem
                label="Average bill rate"
                name="billRate"
                data-id="newClientBillRateField"
              >
                <InputNumber
                  name="billRate"
                  placeholder="Enter average bill rate"
                  precision={2}
                  className="full-width"
                />
              </FormItem>
            </Col>
          </Row>

          <Row gutter={40}>
            <Col xs={24} md={12}>
              <FormItem
                name="haveBusinessBefore"
                data-id="newClientHaveBusinessBeforeField"
                className={styles.businessBefore}
              >
                <span className={styles.text}>
                  Have you done business with them before?
                </span>

                <Radio.Group
                  className="radio"
                  name="haveBusinessBefore"
                  options={[
                    { label: 'Yes', value: true },
                    { label: 'No', value: false },
                  ]}
                />
              </FormItem>
            </Col>
          </Row>
        </Form>
      </>
    </Formik>
  );
});
